
import React, { useState, useEffect } from 'react';
import { ConfirmCloseDialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/ConfirmCloseDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Checkbox } from '@/components/ui/checkbox';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
  org_id?: string;
  consent_gdpr: boolean;
  owner_id?: string;
  created_at: string;
  updated_at: string;
}

interface Organization {
  id: string;
  name: string;
  website?: string | null;
  favicon_url?: string | null;
  logo_url?: string | null;
}

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact?: Contact | null;
  onSave: () => void;
  organizations?: Organization[];
}

export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, contact, onSave, organizations: passedOrganizations }) => {
  const { user, userRole } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fetchedOrganizations, setFetchedOrganizations] = useState<Organization[]>([]);
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    title: '',
    org_id: '',
    consent_gdpr: false,
    visible_for_tenants: [] as string[],
  });

  const { hasUnsavedChanges, resetInitialValues } = useUnsavedChanges(formData, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    
    if (contact) {
      setFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: (contact as any).mobile || '',
        title: contact.title || '',
        org_id: contact.org_id || '',
        consent_gdpr: contact.consent_gdpr || false,
        visible_for_tenants: (contact as any).visible_for_tenants || [],
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        mobile: '',
        title: '',
        org_id: '',
        consent_gdpr: false,
        visible_for_tenants: [],
      });
    }

    // Reset initial values after formData is set to prevent false "unsaved changes" dialogs
    const timer = setTimeout(() => {
      resetInitialValues();
    }, 0);
    return () => clearTimeout(timer);
  }, [contact, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchOrganizations = async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, website, favicon_url, logo_url')
        .is('deleted_at', null)
        .order('name');
      
      if (error) {
        console.error('Error fetching organizations:', error);
        return;
      }
      
      setFetchedOrganizations(data || []);
    };

    if (isOpen) {
      fetchOrganizations();
    }
  }, [isOpen]);

  // Fetch tenants for super_admin
  useEffect(() => {
    const fetchTenants = async () => {
      if (userRole === 'super_admin' && isOpen) {
        const { data, error } = await supabase
          .from('tenants')
          .select('id, name')
          .order('name');
        
        if (!error && data) {
          setTenants(data);
        }
      }
    };
    
    fetchTenants();
  }, [isOpen, userRole]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!user) {
      toast({
        title: 'Fout',
        description: 'Je moet ingelogd zijn om een contact op te slaan.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    
    try {
      const contactData: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile,
        title: formData.title,
        org_id: formData.org_id === 'none' ? null : formData.org_id || null,
        consent_gdpr: formData.consent_gdpr,
        owner_id: user.id
      };

      // Only super_admin can set visible_for_tenants - always send to allow clearing
      if (userRole === 'super_admin') {
        contactData.visible_for_tenants = formData.visible_for_tenants;
      }

      if (contact) {
        const { error } = await supabase
          .from('contacts')
          .update(contactData)
          .eq('id', contact.id);

        if (error) throw error;

        toast({
          title: 'Contact bijgewerkt',
          description: 'Het contact is succesvol bijgewerkt.',
        });
      } else {
        const { error } = await supabase
          .from('contacts')
          .insert(contactData);

        if (error) throw error;

        toast({
          title: 'Contact toegevoegd',
          description: 'Het contact is succesvol toegevoegd.',
        });
      }

      resetInitialValues();
      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error saving contact:', error);
      toast({
        title: 'Fout bij opslaan',
        description: error.message || 'Er ging iets mis bij het opslaan van het contact.',
        variant: 'destructive'
      });
    }
    setLoading(false);
  };
  const getOrgIcon = (org: Organization) => {
    const favicon = org.logo_url || org.favicon_url || (org.website ? 
      `https://www.google.com/s2/favicons?sz=32&domain=${org.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]}` : 
      null);
    
    if (favicon) {
      return (
        <img 
          src={favicon}
          alt=""
          className="h-4 w-4 object-contain flex-shrink-0 rounded-sm"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    
    return (
      <div className="h-4 w-4 rounded bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground flex-shrink-0">
        {org.name.charAt(0).toUpperCase()}
      </div>
    );
  };

  return (
    <ConfirmCloseDialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      hasUnsavedChanges={hasUnsavedChanges}
      onConfirmDiscard={onClose}
      onConfirmSave={handleSubmit}
      isSaving={loading}
    >
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto mx-4 w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>{contact ? 'Contact bewerken' : 'Nieuw contact'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">Voornaam *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="last_name">Achternaam *</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">E-mail *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Telefoon</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="mobile">Mobiel</Label>
              <Input
                id="mobile"
                value={formData.mobile}
                onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Functie</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="org_id">Organisatie</Label>
            <Select value={formData.org_id} onValueChange={(value) => setFormData(prev => ({ ...prev, org_id: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer een organisatie">
                  {formData.org_id && formData.org_id !== 'none' && (() => {
                    const selectedOrg = (passedOrganizations?.length ? passedOrganizations : fetchedOrganizations).find(o => o.id === formData.org_id);
                    return selectedOrg ? (
                      <div className="flex items-center gap-2">
                        {getOrgIcon(selectedOrg)}
                        <span>{selectedOrg.name}</span>
                      </div>
                    ) : null;
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="z-[10020]">
                <SelectItem value="none">Geen organisatie</SelectItem>
                {(passedOrganizations?.length ? passedOrganizations : fetchedOrganizations).map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    <div className="flex items-center gap-2">
                      {getOrgIcon(org)}
                      <span>{org.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="consent_gdpr"
              checked={formData.consent_gdpr}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_gdpr: checked }))}
            />
            <Label htmlFor="consent_gdpr">GDPR toestemming</Label>
          </div>

          {userRole === 'super_admin' && tenants.length > 0 && (
            <div>
              <Label>Zichtbaar voor tenants</Label>
              <div className="space-y-2 mt-2 p-4 border rounded-md">
                {tenants.map((tenant) => (
                  <div key={tenant.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`tenant-${tenant.id}`}
                      checked={formData.visible_for_tenants.includes(tenant.id)}
                      onCheckedChange={(checked) => {
                        setFormData((prev) => ({
                          ...prev,
                          visible_for_tenants: checked
                            ? [...prev.visible_for_tenants, tenant.id]
                            : prev.visible_for_tenants.filter((id) => id !== tenant.id),
                        }));
                      }}
                    />
                    <Label htmlFor={`tenant-${tenant.id}`} className="cursor-pointer font-normal">
                      {tenant.name}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Selecteer voor welke tenants dit contact zichtbaar moet zijn
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Annuleren
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? 'Opslaan...' : contact ? 'Bijwerken' : 'Toevoegen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmCloseDialog>
  );
};
